<template>
    <label
        class="iu-radio-button"
        :class="[
            size ? 'iu-radio-button--' + size : '',
            { 'is-active': value === label },
            { 'is-disabled': isDisabled },
            { 'is-focus': focus },
        ]"
        role="radio"
        :aria-checked="value === label"
        :aria-disabled="isDisabled"
        :tabindex="tabIndex"
        @keydown.space.stop.prevent="value = isDisabled ? value : label"
    >
        <input
            class="iu-radio-button__orig-radio"
            :value="label"
            type="radio"
            v-model="value"
            :name="name"
            @change="handleChange"
            :disabled="isDisabled"
            tabindex="-1"
            @focus="focus = true"
            @blur="focus = false"
        />
        <span class="iu-radio-button__inner" :style="value === label ? activeStyle : null" @keydown.stop>
            <slot></slot>
            <template v-if="!$slots.default">{{ label }}</template>
        </span>
    </label>
</template>
<script>
import Emitter from '../../../utils/emitter';

export default {
    name: 'IuRadioButton',
    mixins: [Emitter],
    inject: {
        elForm: {
            default: '',
        },
        elFormItem: {
            default: '',
        },
    },
    props: {
        label: {},
        disabled: Boolean,
        name: String,
    },
    data() {
        return {
            focus: false,
        };
    },
    computed: {
        value: {
            get() {
                return this._radioGroup.value;
            },
            set(value) {
                this._radioGroup.$emit('input', value);
            },
        },
        _radioGroup() {
            let parent = this.$parent;
            while (parent) {
                if (parent.$options.componentName !== 'IuRadioGroup') {
                    parent = parent.$parent;
                } else {
                    return parent;
                }
            }
            return false;
        },
        activeStyle() {
            return {
                backgroundColor: this._radioGroup.fill || '',
                borderColor: this._radioGroup.fill || '',
                boxShadow: this._radioGroup.fill ? `-1px 0 0 0 ${this._radioGroup.fill}` : '',
                color: this._radioGroup.textColor || '',
            };
        },
        _elFormItemSize() {
            return (this.elFormItem || {}).elFormItemSize;
        },
        size() {
            return this._radioGroup.radioGroupSize || this._elFormItemSize || (this.$ELEMENT || {}).size;
        },
        isDisabled() {
            return this.disabled || this._radioGroup.disabled || (this.elForm || {}).disabled;
        },
        tabIndex() {
            return this.isDisabled || (this._radioGroup && this.value !== this.label) ? -1 : 0;
        },
    },
    methods: {
        handleChange() {
            this.$nextTick(() => {
                this.dispatch('IuRadioGroup', 'handleChange', this.value);
            });
        },
    },
};
</script>

<style scope>
@charset "UTF-8";
.iu-radio-button,
.iu-radio-button__inner {
    display: inline-block;
    position: relative;
    outline: 0;
}
.iu-radio-button__inner {
    line-height: 1;
    white-space: nowrap;
    vertical-align: middle;
    background: #fff;
    border: 1px solid #dcdfe6;
    font-weight: 500;
    border-left: 0;
    color: #606266;
    -webkit-appearance: none;
    text-align: center;
    -webkit-box-sizing: border-box;
    box-sizing: border-box;
    margin: 0;
    cursor: pointer;
    -webkit-transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
    padding: 12px 20px;
    font-size: 14px;
    border-radius: 0;
}
.iu-radio-button__inner.is-round {
    padding: 12px 20px;
}
.iu-radio-button__inner:hover {
    color: #409eff;
}
.iu-radio-button__inner [class*='iu-icon-'] {
    line-height: 0.9;
}
.iu-radio-button__inner [class*='iu-icon-'] + span {
    margin-left: 5px;
}
.iu-radio-button:first-child .iu-radio-button__inner {
    border-left: 1px solid #dcdfe6;
    border-radius: 4px 0 0 4px;
    -webkit-box-shadow: none !important;
    box-shadow: none !important;
}
.iu-radio-button__orig-radio {
    opacity: 0;
    outline: 0;
    position: absolute;
    z-index: -1;
}
.iu-radio-button__orig-radio:checked + .iu-radio-button__inner {
    color: #fff;
    background-color: #409eff;
    border-color: #409eff;
    -webkit-box-shadow: -1px 0 0 0 #409eff;
    box-shadow: -1px 0 0 0 #409eff;
}
.iu-radio-button__orig-radio:disabled + .iu-radio-button__inner {
    color: #c0c4cc;
    cursor: not-allowed;
    background-image: none;
    background-color: #fff;
    border-color: #ebeef5;
    -webkit-box-shadow: none;
    box-shadow: none;
}
.iu-radio-button__orig-radio:disabled:checked + .iu-radio-button__inner {
    background-color: #f2f6fc;
}
.iu-radio-button:last-child .iu-radio-button__inner {
    border-radius: 0 4px 4px 0;
}
.iu-radio-button:first-child:last-child .iu-radio-button__inner {
    border-radius: 4px;
}
.iu-radio-button--medium .iu-radio-button__inner {
    padding: 10px 20px;
    font-size: 14px;
    border-radius: 0;
}
.iu-radio-button--medium .iu-radio-button__inner.is-round {
    padding: 10px 20px;
}
.iu-radio-button--small .iu-radio-button__inner {
    padding: 9px 15px;
    font-size: 12px;
    border-radius: 0;
}
.iu-radio-button--small .iu-radio-button__inner.is-round {
    padding: 9px 15px;
}
.iu-radio-button--mini .iu-radio-button__inner {
    padding: 7px 15px;
    font-size: 12px;
    border-radius: 0;
}
.iu-radio-button--mini .iu-radio-button__inner.is-round {
    padding: 7px 15px;
}
.iu-radio-button:focus:not(.is-focus):not(:active):not(.is-disabled) {
    -webkit-box-shadow: 0 0 2px 2px #409eff;
    box-shadow: 0 0 2px 2px #409eff;
}
</style>
